/**
 * Routen der SPA. Pfade kommen aus screens.ts (fest); jede Seite hängt hier.
 * Rollen: Navigation zeigt nur erlaubte Screens; direkte Aufrufe ohne Rolle
 * landen auf „Kein Zugriff“ (die API prüft zusätzlich, Ruling R22).
 */
import { useEffect, type ComponentType } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLLEN_RANG } from '@storeos/contracts';
import { me } from './api';
import { Shell } from './components/Shell';
import { BenutzerKontext } from './components/ui';
import { Login } from './pages/Login';
import { Platzhalter } from './pages/Platzhalter';
import { Dashboard } from './pages/Dashboard';
import { Auftraege } from './pages/Auftraege';
import { Packstation } from './pages/Packstation';
import { TagesabschlussSeite } from './pages/Tagesabschluss';
import { ProduktionSeite } from './pages/Produktion';
import { Artikel } from './pages/Artikel';
import { Wareneingang } from './pages/Wareneingang';
import { InventurSeite } from './pages/Inventur';
import { InventurWeiterSeite } from './pages/InventurWeiter';
import { ProduktionErfassen } from './pages/ProduktionErfassen';
import { Retouren } from './pages/Retouren';
import { Belege } from './pages/Belege';
import { Protokoll } from './pages/Protokoll';
import { EinstellungenSeite } from './pages/Einstellungen';
import { PRODUKTION_START, SCREENS } from './screens';

const SEITEN: Record<string, ComponentType> = {
  '/': Dashboard,
  '/auftraege': Auftraege,
  '/packstation': Packstation,
  '/tagesabschluss': TagesabschlussSeite,
  '/produktion': ProduktionSeite,
  '/produktion/erfassen': ProduktionErfassen,
  '/artikel': Artikel,
  '/wareneingang': Wareneingang,
  '/inventur': InventurSeite,
  '/retouren': Retouren,
  '/belege': Belege,
  '/protokoll': Protokoll,
  '/einstellungen': EinstellungenSeite,
};

export function App() {
  const navigate = useNavigate();
  const ort = useLocation();
  const qc = useQueryClient();
  const user = useQuery({ queryKey: ['me'], queryFn: me, retry: false });

  useEffect(() => {
    const h = () => { qc.setQueryData(['me'], null); navigate('/login'); };
    window.addEventListener('storeos:unauthorized', h);
    return () => window.removeEventListener('storeos:unauthorized', h);
  }, [navigate, qc]);

  if (user.isLoading) return <div className="laden">StoreOS lädt …</div>;

  if (!user.data) {
    return (
      <Routes>
        <Route path="/login" element={<Login onLogin={() => { qc.clear(); return user.refetch(); }} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const abmelden = () => { qc.setQueryData(['me'], null); qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' }); navigate('/login'); };
  // R127: Die Druckerei (Rolle produktion) sieht nur „Produktion erfassen“ und landet nach dem Login direkt dort.
  if (user.data.rolle === 'produktion') {
    return (
      <BenutzerKontext.Provider value={user.data}>
        <Shell user={user.data} onLogout={abmelden}>
          <Routes>
            <Route path={PRODUKTION_START} element={<ProduktionErfassen />} />
            <Route path="*" element={<Navigate to={PRODUKTION_START} replace />} />
          </Routes>
        </Shell>
      </BenutzerKontext.Provider>
    );
  }
  const rang = ROLLEN_RANG[user.data.rolle];
  // R126: geführte Erstinventur am Tablet im Vollbild, ohne Navigation (Rollen lager und leitung).
  if (ort.pathname === '/inventur/weiter' && rang >= ROLLEN_RANG.lager) {
    return <BenutzerKontext.Provider value={user.data}><InventurWeiterSeite /></BenutzerKontext.Provider>;
  }
  return (
    <BenutzerKontext.Provider value={user.data}>
      <Shell user={user.data} onLogout={abmelden}>
        <Routes>
          {SCREENS.map((s) => {
            const Seite = SEITEN[s.pfad] ?? (() => <Platzhalter name={s.name} />);
            const erlaubt = rang >= ROLLEN_RANG[s.rolle];
            return <Route key={s.pfad} path={s.pfad === '/' ? '/' : `${s.pfad}/*`} element={erlaubt ? <Seite /> : <Platzhalter name="Kein Zugriff" text={`${s.name} braucht mindestens die Rolle ${s.rolle}.`} />} />;
          })}
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Platzhalter name="Seite nicht gefunden" />} />
        </Routes>
      </Shell>
    </BenutzerKontext.Provider>
  );
}
